import PropTypes from 'prop-types';
import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { whatsappConversationAPI } from '../../../../helpers/whatsappApiHandler';

const AssignDropdown = memo(
  ({
    conversationId,
    currentAssignee,
    agents = [],
    agentsLoading = false,
    agentsError = null,
    onAssignToMe,
    onUnassign,
    onAssignToAgent,
    disabled = false,
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Get access token and current user from Redux store
    const accessToken = useSelector((state) => state.auth.access_token?.token);
    const currentUserId = useSelector((state) => state.auth.user?.id);

    // Filter out the current user from the agents list
    const filteredAgents = agents.filter((agent) => agent.id !== currentUserId);

    const handleDropdownToggle = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    };

    const handleAssignToMe = async () => {
      try {
        if (currentUserId && conversationId) {
          const response = await whatsappConversationAPI.assignConversation(
            conversationId,
            currentUserId,
            accessToken
          );
          if (response.success) {
            if (onAssignToMe) {
              onAssignToMe(conversationId);
            }
          } else {
            console.error('Failed to assign conversation to me:', response.message);
          }
        }
        setIsOpen(false);
      } catch (error) {
        console.error('Error assigning to me:', error);
      }
    };

    const handleUnassign = async () => {
      try {
        if (conversationId) {
          const response = await whatsappConversationAPI.unassignConversation(
            conversationId,
            accessToken
          );
          if (response.success) {
            if (onUnassign) {
              onUnassign(conversationId);
            }
          } else {
            console.error('Failed to unassign conversation:', response.message);
          }
        }
        setIsOpen(false);
      } catch (error) {
        console.error('Error unassigning:', error);
      }
    };

    const handleAssignToAgent = async (agent) => {
      try {
        if (conversationId && agent.id) {
          const response = await whatsappConversationAPI.assignConversation(
            conversationId,
            agent.id,
            accessToken
          );
          if (response.success) {
            if (onAssignToAgent) {
              onAssignToAgent(conversationId, agent);
            }
          } else {
            console.error('Failed to assign conversation to agent:', response.message);
          }
        } else {
          console.error('Missing conversationId or agent.id:', {
            conversationId,
            agentId: agent.id,
          });
        }
        setIsOpen(false);
      } catch (error) {
        console.error('Error assigning to agent:', error);
      }
    };

    const handleMouseEnter = (e) => {
      e.target.style.backgroundColor = '#F9FAFB';
    };

    const handleMouseLeave = (e) => {
      e.target.style.backgroundColor = 'transparent';
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (event.target.closest('.assign-dropdown-container') === null) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    return (
      <div className="assign-dropdown-container" style={{ position: 'relative' }}>
        {/* Dropdown Trigger Button */}
        <button
          type="button"
          onClick={handleDropdownToggle}
          disabled={disabled}
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 12px 4px 10px',
            minWidth: '160px',
            width: 'auto',
            backgroundColor: '#FFFFFF',
            border: '1px solid #D0D5DD',
            borderRadius: '4px',
            fontFamily: 'Poppins',
            fontWeight: '400',
            fontSize: '13px',
            color: disabled ? '#98A2B3' : '#737C8A',
            cursor: disabled ? 'not-allowed' : 'pointer',
            boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              flex: 1,
            }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                fill={disabled ? '#98A2B3' : '#38C669'}
              />
              <path
                d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z"
                fill={disabled ? '#98A2B3' : '#38C669'}
              />
            </svg>
            {currentAssignee ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '2px',
                  flex: 1,
                }}
              >
                <span
                  style={{
                    fontFamily: 'Poppins',
                    fontWeight: '500',
                    fontSize: '13px',
                    color: disabled ? '#98A2B3' : '#344054',
                    lineHeight: '1.2em',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '200px',
                  }}
                >
                  {currentAssignee.name}
                </span>
                <span
                  style={{
                    fontFamily: 'Poppins',
                    fontWeight: '400',
                    fontSize: '11px',
                    color: disabled ? '#98A2B3' : '#667085',
                    lineHeight: '1.2em',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '200px',
                  }}
                >
                  {currentAssignee.email}
                </span>
              </div>
            ) : (
              <span
                style={{
                  fontFamily: 'Poppins',
                  fontWeight: '400',
                  fontSize: '13px',
                  color: disabled ? '#98A2B3' : '#737C8A',
                }}
              >
                Assign
              </span>
            )}
          </div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path d="M7 10L12 15L17 10H7Z" fill={disabled ? '#98A2B3' : '#737C8A'} />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '0',
              right: '0',
              minWidth: '200px',
              width: 'auto',
              backgroundColor: '#FFFFFF',
              border: '1px solid #D0D5DD',
              borderRadius: '4px',
              boxShadow: '0px 4px 6px rgba(16, 24, 40, 0.1)',
              zIndex: 1000,
              maxHeight: '300px',
              overflowY: 'auto',
              marginTop: '4px',
            }}
          >
            {/* Assign to Me Option */}
            <button
              type="button"
              onClick={handleAssignToMe}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px 14px',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid #F2F4F7',
                fontFamily: 'Poppins',
                fontWeight: '400',
                fontSize: '12px',
                color: '#344054',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                  fill="#38C669"
                />
                <path
                  d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z"
                  fill="#38C669"
                />
              </svg>
              Assign to Me
            </button>

            {/* Unassign Option */}
            <button
              type="button"
              onClick={handleUnassign}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px 14px',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: agents.length > 0 ? '1px solid #F2F4F7' : 'none',
                fontFamily: 'Poppins',
                fontWeight: '400',
                fontSize: '12px',
                color: '#344054',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#98A2B3"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Unassign
            </button>

            {/* Loading State */}
            {agentsLoading && (
              <div
                style={{
                  padding: '10px 14px',
                  textAlign: 'center',
                  color: '#667085',
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                }}
              >
                Loading agents...
              </div>
            )}

            {/* Error State */}
            {agentsError && (
              <div
                style={{
                  padding: '10px 14px',
                  textAlign: 'center',
                  color: '#D92D20',
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                }}
              >
                {agentsError}
              </div>
            )}

            {/* Agent Options */}
            {!agentsLoading &&
              !agentsError &&
              filteredAgents.map((agent) => (
                <button
                  type="button"
                  key={agent.id}
                  onClick={() => handleAssignToAgent(agent)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '10px 14px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid #F2F4F7',
                    fontFamily: 'Poppins',
                    fontWeight: '400',
                    fontSize: '12px',
                    color: '#344054',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                      fill="#38C669"
                    />
                    <path
                      d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z"
                      fill="#38C669"
                    />
                  </svg>
                  <span style={{ flex: 1 }}>{agent.name}</span>
                  {currentAssignee && currentAssignee.id === agent.id && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                        fill="#12B76A"
                      />
                    </svg>
                  )}
                </button>
              ))}

            {/* No Agents Message */}
            {!agentsLoading && !agentsError && filteredAgents.length === 0 && (
              <div
                style={{
                  padding: '10px 14px',
                  textAlign: 'center',
                  color: '#667085',
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                }}
              >
                No agents available
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

AssignDropdown.propTypes = {
  conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currentAssignee: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      email: PropTypes.string,
      lastActive: PropTypes.string,
      role: PropTypes.string,
      status: PropTypes.string,
      assignedAt: PropTypes.string,
    })
  ),
  agentsLoading: PropTypes.bool,
  agentsError: PropTypes.string,
  onAssignToMe: PropTypes.func,
  onUnassign: PropTypes.func,
  onAssignToAgent: PropTypes.func,
  disabled: PropTypes.bool,
};

AssignDropdown.displayName = 'AssignDropdown';

export default AssignDropdown;
